import React from "react";
import PageHero from "@/components/site/PageHero";
import { Reveal } from "@/components/site/Reveal";
import { CONSENT_OPEN } from "@/lib/track";

const UPDATED = "8 October 2026";
const EMAIL = "hello@nomus.work";

// Each entry: what it is, then what happens. Keep it true to the code:
// cms/contact.mjs (what a message stores) and public/index.html (analytics).
const SECTIONS = [
  [
    "Who is responsible",
    <>
      Nomus is operated by Goodvibe OÜ, a private limited company registered in
      Estonia under registry code 14101638, at Sepapaja tn 6, Tallinn. Mariusz
      Czajkowski runs it. For anything on this page, write to{" "}
      <a href={`mailto:${EMAIL}`}>{EMAIL}</a>.
    </>,
  ],
  [
    "When you write to us",
    <>
      The contact form stores what you type: your name, email, company, how the
      work happens today, the topics you pick and your message, with the page
      you sent it from. To limit abuse it also stores a one-way code made from
      your network address, which changes every day. We use all of this to reply
      and to work with you, and for nothing else.
    </>,
  ],
  [
    "When you book, sign and pay",
    <>
      Booking the Audit stores your company&rsquo;s name and address, your name
      and your work email, and opens a private page for your Audit. When you
      sign the agreement there, we record your name, role and email, the time,
      your network address and your browser, as proof of signing. Stripe takes
      the payment; we receive your name, email, company and the amount, and
      never your card details. Resend delivers the few emails we send you about
      your Audit: the link to your page, your copy of the agreement, and a note
      when something is waiting for you. What you and we write on your private
      page is stored with it, and is shown only to the people you let in. The
      same goes for files you send us there: they are kept in Cloudflare&rsquo;s
      file storage in the European Union, can be opened only by the people
      signed in to your page and by us, and are deleted when you remove them or
      when your page is deleted. You sign in to it with a link we email you. So
      that you do not have to do that on every visit, the browser you used keeps
      one sign-in cookie for 60 days, or until you sign out. The page cannot
      work without it, it is not used to measure anything, and it is the only
      cookie we set without asking.
    </>,
  ],
  [
    "The check",
    <>
      Your answers stay in your browser and in the link to your result. They
      reach us only if you leave your email, write to us from the result, or
      book a call from it. Then a one-line summary of the result comes with your
      message. If you allowed measuring, we also see how the check ended (the
      job, the hours and the result), without your name.
    </>,
  ],
  [
    "Measuring the site",
    <>
      Only if you allow it. We use PostHog, on servers in the European Union, to
      count visits, see which buttons are used, see when a check is started and
      how it ends, and record sessions so we can find what is broken. Text typed
      into forms is hidden in those recordings. If you allow it and later give
      us your email, your visits are linked to that email. PostHog keeps this in
      cookies and in your browser&rsquo;s storage. If you decline, none of it is
      loaded.
    </>,
  ],
  [
    "Booking a call",
    <>
      The calendar is run by Cal.com. What you enter there goes to Cal.com and
      to us, under Cal.com&rsquo;s own privacy policy.
    </>,
  ],
  [
    "Who else handles it",
    <>
      Cloudflare serves this site and stores the messages you send. Each new
      message is also passed to the assistant that helps us answer it, which
      runs on Cursor. Google serves the fonts, so Google receives your network
      address when a page loads. PostHog, Cal.com, Stripe and Resend are
      described above. Cloudflare, Cursor, Google, Cal.com, Stripe and Resend
      are American companies and may process data outside the European Union
      under their own safeguards. We sell nothing and show no ads.
    </>,
  ],
  [
    "How long we keep it",
    <>
      Messages stay until you ask us to delete them. Your private Audit page and
      the files on it stay until you ask us to delete them; write to
      hello@nomus.work and we do it. Measurement data is deleted on
      PostHog&rsquo;s schedule for our plan, or sooner if you ask.
    </>,
  ],
  [
    "Why we may",
    <>
      Measuring rests on your consent, which you can withdraw at any time.
      Replying to your message rests on your request and on our interest in
      answering it.
    </>,
  ],
  [
    "Your rights",
    <>
      You can ask what we hold about you, have it corrected or deleted, object
      to its use, or get a copy. Write to{" "}
      <a href={`mailto:${EMAIL}`}>{EMAIL}</a>. You can also complain to the data
      protection authority where you live, or to the Estonian Data Protection
      Inspectorate (Andmekaitse Inspektsioon).
    </>,
  ],
];

export default function Privacy() {
  return (
    <>
      <PageHero
        label="Privacy"
        title="What we collect, and why."
        text="Nothing is measured unless you allow it. We get your email only if you give it."
      />
      <section className="px-5 pt-16 lg:pt-28" data-testid="privacy">
        <div className="mx-auto max-w-[1400px]">
          <dl className="border-t border-black/10">
            {SECTIONS.map(([name, text], i) => (
              <Reveal
                key={name}
                delay={Math.min(i, 4) * 60}
                className="grid gap-2 border-b border-black/10 py-7 sm:py-9 lg:grid-cols-12 lg:gap-8"
              >
                <dt className="text-[22px] leading-[1.15] tracking-[-0.02em] sm:text-[28px] lg:col-span-4">
                  {name}
                </dt>
                <dd className="max-w-[760px] text-[17px] leading-[1.45] opacity-60 sm:text-[19px] lg:col-span-8 [&_a]:underline [&_a]:underline-offset-2">
                  {text}
                </dd>
              </Reveal>
            ))}
          </dl>
          <div className="mt-10 flex flex-wrap items-center justify-between gap-4">
            <button
              type="button"
              data-testid="privacy-settings"
              onClick={() => window.dispatchEvent(new Event(CONSENT_OPEN))}
              className="h-11 rounded-full bg-[#f7f7f7] px-6 text-[13px] font-medium transition-colors hover:bg-black hover:text-white"
            >
              Change your cookie choice
            </button>
            <p className="text-[13px] opacity-45">Last updated {UPDATED}.</p>
          </div>
        </div>
      </section>
    </>
  );
}
